package one.healthium.data.user.storage

import co.touchlab.kermit.Logger
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.storage.Data
import dev.gitlive.firebase.storage.FirebaseStorageMetadata
import dev.gitlive.firebase.storage.StorageReference
import dev.gitlive.firebase.storage.storage
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.withContext
import one.healthium.base.coroutines.IO
import one.primehealth.app.BaseAppConfig

/**
 * Storage helper to upload user avatar to Firebase Storage.
 * Saves file under `avatars/{userId}.jpg` and returns the public download URL.
 */
class FirebaseAvatarStorage(
    baseAppConfig: BaseAppConfig,
) {

    private val logger = Logger.withTag("FirebaseAvatarStorage")
    private val storage by lazy { Firebase.storage(baseAppConfig.firebaseApp.userStorageBucket) }

    /**
     * Uploads avatar bytes for the given userId and returns a download URL.
     * Uses a `.jpg` extension by default. You may pass a different contentType
     * (for example "image/png").
     */
    suspend fun uploadAvatar(userId: String, image: ByteArray, contentType: String = "image/jpeg"): String {
        return withContext(Dispatchers.IO) {
            try {
                val ref = getImageFileReference(userId)

                val metadata = FirebaseStorageMetadata(contentType = contentType)

                ref.putData(image.toData(), metadata)

                val url = ref.getDownloadUrl()
                logger.d("uploaded avatar for user=$userId url=$url")
                url
            } catch (e: Exception) {
                logger.e("failed to upload avatar for user=$userId", e)
                throw e
            }
        }
    }

    suspend fun deleteAvatar(userId: String) {
        getImageFileReference(userId).delete()
    }

    private fun getImageFileReference(userId: String): StorageReference {
        val path = "avatars/$userId.jpg"
        val ref = storage.reference(path)
        return ref
    }
}

internal expect fun ByteArray.toData(): Data